import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Package, Menu, X, LogOut, Heart, ChevronDown, Globe, MessageCircle, Phone, ShieldCheck, Award, Truck, MapPin, Sun, Moon } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useSite } from "../contexts/SiteContext";
import { useLocale } from "../contexts/LocaleContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useTheme } from "../contexts/ThemeContext";
import useContent from "../lib/useContent";
import TrackShipmentModal from "./TrackShipmentModal";

const TRUST_ITEMS = [
  { icon: ShieldCheck, label: "Trusted Platform" },
  { icon: Award, label: "Authenticity Certified" },
  { icon: ShieldCheck, label: "Certified Warranty" },
  { icon: Truck, label: "Delivery & Returns" },
  { icon: MapPin, label: "Physical Showroom" },
  { icon: MessageCircle, label: "WhatsApp Support" },
];

const DEFAULT_NAV = [
  { label: "shop_by_brands", href: "/shop", dropdown: ["Rolex","Patek Philippe","Audemars Piguet","Omega","Cartier","TAG Heuer","Hublot","IWC"] },
  { label: "watches", href: "/shop", dropdown: ["Luxury","Sport","Diving","Chronograph","Dress","Limited Edition"] },
  { label: "handbags", href: "/shop?category=handbags", dropdown: ["Hermès","Chanel","Louis Vuitton","Gucci","Prada"] },
  { label: "leather_goods", href: "/p/leather-goods" },
  { label: "accessories", href: "/p/accessories", dropdown: ["Cufflinks","Pens","Wallets","Belts"] },
  { label: "brand_new", href: "/shop?featured=true" },
  { label: "blog", href: "/p/blog" },
];

const Dropdown = ({ label, items, baseHref, ddOpen, setDdOpen, idx, onClickItem }) => {
  const open = ddOpen === idx;
  const normalized = (items || []).map((it) => typeof it === "string" ? { label: it, href: `${baseHref}?q=${encodeURIComponent(it)}` } : it);
  return (
    <div className="relative" onMouseEnter={() => setDdOpen(idx)} onMouseLeave={() => setDdOpen(null)}>
      <Link to={baseHref} className="flex items-center gap-1 hover:text-copper transition-colors py-2">
        {label} {normalized.length > 0 && <ChevronDown size={11} className="opacity-60"/>}
      </Link>
      {normalized.length > 0 && open && (
        <div className="absolute top-full left-0 z-30 min-w-[220px] bg-white shadow-xl border border-black/5 py-2">
          {normalized.map((it, i) => (
            it.href?.startsWith("http") ? (
              <a key={`${it.label}-${i}`} href={it.href} target="_blank" rel="noreferrer" onClick={onClickItem} className="block px-5 py-2.5 text-[12px] tracking-wider text-gray-700 hover:bg-[#FAF7F2] hover:text-copper transition-colors">{it.label}</a>
            ) : (
              <Link key={`${it.label}-${i}`} to={it.href || "#"} onClick={onClickItem} className="block px-5 py-2.5 text-[12px] tracking-wider text-gray-700 hover:bg-[#FAF7F2] hover:text-copper transition-colors">{it.label}</Link>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const SwitchDropdown = ({ trigger, children, openSwitch, setOpenSwitch, name }) => {
  const open = openSwitch === name;
  return (
    <div className="relative">
      <button data-testid={`switch-${name}`} onClick={() => setOpenSwitch(open ? null : name)} className="flex items-center gap-1.5 px-3 py-2 border border-black/15 hover:border-copper text-[11px] uppercase tracking-luxe text-gray-700 bg-white">
        {trigger} <ChevronDown size={11} className="opacity-60"/>
      </button>
      {open && (
        <div className="absolute top-full right-0 z-30 mt-1 min-w-[180px] bg-white shadow-xl border border-black/10 py-1">
          {children}
        </div>
      )}
    </div>
  );
};

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSite();
  const { t, lang, setLang, currency, setCurrency, currencies, rates } = useLocale();
  const { count: wlCount } = useWishlist();
  const { theme, toggle: toggleTheme, isDark } = useTheme();
  const { data: navContent } = useContent("nav_menu", { items: DEFAULT_NAV });
  const NAV = (navContent?.items?.length ? navContent.items : DEFAULT_NAV).map((it) => ({
    label: it.label || "",
    href: it.href || "/shop",
    // Only treat lowercased snake_case strings as i18n keys; CMS labels render verbatim.
    isI18nKey: typeof it.label === "string" && /^[a-z][a-z0-9_]+$/.test(it.label),
    dropdown: Array.isArray(it.sublinks) && it.sublinks.length ? it.sublinks : (it.dropdown && it.dropdown.length ? it.dropdown : null),
  }));
  const [trackOpen, setTrackOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(null);
  const [openSwitch, setOpenSwitch] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState("All Categories");
  const nav = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set("q", searchQuery);
    if (searchCategory && searchCategory !== "All Categories") params.set("category", searchCategory.toLowerCase());
    nav(`/shop?${params.toString()}`);
  };

  return (
    <>
      {/* Trust bar (top dark) */}
      <div className="trust-bar">
        {/* Desktop / wide tablet: centered grid, no overflow */}
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-3 hidden lg:flex items-center justify-center gap-8 xl:gap-12">
          {TRUST_ITEMS.map((it, i) => (
            <div key={i} className="trust-item flex items-center gap-2 text-[10.5px] uppercase tracking-luxe whitespace-nowrap">
              <it.icon size={13} className="icon"/> {it.label}
            </div>
          ))}
        </div>
        {/* Mobile / tablet: smooth marquee */}
        <div className="lg:hidden overflow-hidden py-2.5">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...TRUST_ITEMS, ...TRUST_ITEMS].map((it, i) => (
              <div key={i} className="trust-item mx-5 flex items-center gap-2 text-[10px] uppercase tracking-luxe shrink-0">
                <it.icon size={11} className="icon"/> {it.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Announcement */}
      {settings.announcement_enabled && settings.announcement_text && (
        <div data-testid="announcement-bar" className="bg-[#FAF7F2] border-b border-black/5 text-center py-2 text-[11px] tracking-luxe text-copper uppercase font-medium">
          {settings.announcement_text}
        </div>
      )}

      {/* Main row: logo + search + icons */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-5 flex items-center gap-6">
          <button data-testid="mobile-menu-btn" className="lg:hidden text-gray-700" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>

          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
            {settings.display_mode === "logo" && settings.logo_url ? (
              <img src={settings.logo_url} alt={settings.site_name || "Logo"} className="h-10 md:h-12 object-contain"/>
            ) : (
              <span className="font-display text-2xl md:text-3xl tracking-wider text-dark">{settings.site_name || "NAMOS ANTIQUES"}</span>
            )}
          </Link>

          {/* Search bar */}
          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl mx-auto items-stretch border border-black/15 bg-white">
            <select data-testid="search-category" value={searchCategory} onChange={(e)=>setSearchCategory(e.target.value)} className="bg-white px-4 py-3 text-[12px] uppercase tracking-luxe text-gray-700 border-r border-black/15 outline-none cursor-pointer">
              <option>All Categories</option><option>Luxury</option><option>Sport</option><option>Dress</option><option>Diving</option><option>Chronograph</option>
            </select>
            <input data-testid="search-input" value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} placeholder={t("search_placeholder")} className="flex-1 px-4 py-3 outline-none text-sm bg-white"/>
            <button data-testid="search-submit" type="submit" className="bg-dark text-white px-5 hover:bg-copper transition-colors"><Search size={16}/></button>
          </form>

          {/* Right icons */}
          <div className="flex items-center gap-5 ml-auto">
            <Link to="/wishlist" data-testid="wishlist-link" className="hidden md:flex flex-col items-center text-gray-700 hover:text-copper transition-colors relative">
              <Heart size={19}/>
              {wlCount > 0 && <span className="absolute -top-1.5 -right-2 bg-copper text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{wlCount}</span>}
              <span className="text-[10px] uppercase tracking-luxe mt-1">{t("wishlist")}</span>
            </Link>
            <Link to="/cart" data-testid="cart-link" className="flex flex-col items-center text-gray-700 hover:text-copper transition-colors relative">
              <ShoppingBag size={19}/>
              {count > 0 && <span data-testid="cart-count" className="absolute -top-1.5 -right-2 bg-copper text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              <span className="text-[10px] uppercase tracking-luxe mt-1 hidden md:block">{t("cart")}</span>
            </Link>
            <button data-testid="track-shipment-btn" onClick={() => setTrackOpen(true)} className="hidden md:flex flex-col items-center text-gray-700 hover:text-copper transition-colors">
              <Package size={19}/>
              <span className="text-[10px] uppercase tracking-luxe mt-1">{t("track_order")}</span>
            </button>
            <div className="relative">
              {user ? (
                <button data-testid="user-menu-btn" onClick={()=>setUserOpen(!userOpen)} className="flex flex-col items-center text-gray-700 hover:text-copper">
                  <User size={19}/><span className="hidden md:block text-[10px] uppercase tracking-luxe mt-1">{t("account")}</span>
                </button>
              ) : (
                <Link to="/login" data-testid="login-link" className="flex flex-col items-center text-gray-700 hover:text-copper">
                  <User size={19}/><span className="hidden md:block text-[10px] uppercase tracking-luxe mt-1">{t("account")}</span>
                </Link>
              )}
              {user && userOpen && (
                <div className="absolute right-0 mt-3 w-56 bg-white border border-black/10 shadow-xl py-1">
                  <div className="px-3 py-2 border-b border-black/5">
                    <div className="text-[11px] uppercase tracking-luxe text-gray-400">Signed in</div>
                    <div className="text-sm text-dark truncate">{user.email}</div>
                    <div className="text-[10px] uppercase tracking-luxe text-copper mt-1">{user.role}</div>
                  </div>
                  {user.role === "developer" && <Link to="/developer" className="block px-3 py-2 text-sm text-gray-700 hover:bg-[#FAF7F2]">Developer Dashboard</Link>}
                  {(user.role === "developer" || user.role === "admin") && <Link to="/admin" className="block px-3 py-2 text-sm text-gray-700 hover:bg-[#FAF7F2]">Admin Dashboard</Link>}
                  {(user.role === "developer" || user.role === "admin" || user.role === "shop_assistant") && <Link to="/shop-dashboard" className="block px-3 py-2 text-sm text-gray-700 hover:bg-[#FAF7F2]">Shop Dashboard</Link>}
                  <button onClick={()=>{logout(); nav("/");}} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#FAF7F2] flex items-center gap-2"><LogOut size={13}/> Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tertiary: nav + currency/language/support — hidden on mobile, controls move to mobile drawer */}
      <div className="tertiary-bar sticky top-0 z-30 hidden md:block">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-2 flex items-center justify-between gap-6">
          <nav className="hidden lg:flex items-center gap-8 text-[11.5px] uppercase tracking-luxe text-gray-700 font-medium">
            {NAV.map((l, i) => (
              <Dropdown key={`${l.label}-${i}`} label={l.isI18nKey ? t(l.label) : l.label} items={l.dropdown} baseHref={l.href} ddOpen={ddOpen} setDdOpen={setDdOpen} idx={i} onClickItem={()=>setDdOpen(null)}/>
            ))}
          </nav>
          <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
            <button data-testid="theme-toggle" onClick={toggleTheme} title={isDark? "Switch to light" : "Switch to dark"} className="flex items-center gap-1.5 px-3 py-2 border border-black/15 hover:border-copper text-[11px] uppercase tracking-luxe text-gray-700 bg-white">
              {isDark ? <><Sun size={12}/> Light</> : <><Moon size={12}/> Dark</>}
            </button>
            <SwitchDropdown name="currency" trigger={rates[currency]?.label || currency} openSwitch={openSwitch} setOpenSwitch={setOpenSwitch}>
              {currencies.map(c => (
                <button data-testid={`currency-${c}`} key={c} onClick={()=>{setCurrency(c); setOpenSwitch(null);}} className={`block w-full text-left px-4 py-2 text-[11.5px] tracking-wider hover:bg-[#FAF7F2] ${currency===c?"text-copper bg-[#FAF7F2]":"text-gray-700"}`}>{rates[c].label}</button>
              ))}
            </SwitchDropdown>
            <SwitchDropdown name="support" trigger={<><Phone size={11}/> {t("support")}</>} openSwitch={openSwitch} setOpenSwitch={setOpenSwitch}>
              <a href={`tel:${settings.contact_phone||""}`} className="block px-4 py-2 text-[11.5px] tracking-wider text-gray-700 hover:bg-[#FAF7F2]">📞 Call us</a>
              <a href="https://wa.me/971000000000" target="_blank" rel="noreferrer" className="block px-4 py-2 text-[11.5px] tracking-wider text-gray-700 hover:bg-[#FAF7F2]">💬 WhatsApp</a>
              <a href={`mailto:${settings.contact_email||""}`} className="block px-4 py-2 text-[11.5px] tracking-wider text-gray-700 hover:bg-[#FAF7F2]">✉ Email</a>
            </SwitchDropdown>
            <SwitchDropdown name="lang" trigger={<><Globe size={11}/> {lang === "ar" ? "العربية" : "English"}</>} openSwitch={openSwitch} setOpenSwitch={setOpenSwitch}>
              <button data-testid="lang-en" onClick={()=>{setLang("en"); setOpenSwitch(null);}} className={`block w-full text-left px-4 py-2 text-[11.5px] tracking-wider hover:bg-[#FAF7F2] ${lang==="en"?"text-copper bg-[#FAF7F2]":"text-gray-700"}`}>English</button>
              <button data-testid="lang-ar" onClick={()=>{setLang("ar"); setOpenSwitch(null);}} className={`block w-full text-left px-4 py-2 text-[11.5px] tracking-wider hover:bg-[#FAF7F2] ${lang==="ar"?"text-copper bg-[#FAF7F2]":"text-gray-700"}`}>العربية</button>
            </SwitchDropdown>
          </div>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-b border-black/10 px-6 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <form onSubmit={onSearch} className="flex border border-black/15">
            <input value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} placeholder={t("search_placeholder")} className="flex-1 px-3 py-2 outline-none text-sm"/>
            <button type="submit" className="bg-dark text-white px-3"><Search size={14}/></button>
          </form>
          <div className="space-y-2">
            {NAV.map((l, i) => <Link key={`${l.label}-${i}`} to={l.href} onClick={()=>setMenuOpen(false)} className="block text-gray-700 text-sm uppercase tracking-luxe py-1">{l.isI18nKey ? t(l.label) : l.label}</Link>)}
            <Link to="/track" onClick={()=>setMenuOpen(false)} className="block text-gray-700 text-sm uppercase tracking-luxe py-1">{t("track_order")}</Link>
            <Link to="/wishlist" onClick={()=>setMenuOpen(false)} className="block text-gray-700 text-sm uppercase tracking-luxe py-1">{t("wishlist")}</Link>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-black/10">
            <button data-testid="theme-toggle-mobile" onClick={toggleTheme} className="flex items-center justify-center gap-1.5 px-3 py-2 border border-black/15 text-[11px] uppercase tracking-luxe text-gray-700">
              {isDark ? <><Sun size={12}/> Light</> : <><Moon size={12}/> Dark</>}
            </button>
            <select className="px-3 py-2 border border-black/15 text-[11px] uppercase tracking-luxe text-gray-700 bg-white" value={currency} onChange={(e)=>setCurrency(e.target.value)}>
              {currencies.map(c => <option key={c} value={c}>{rates[c].label}</option>)}
            </select>
            <select className="px-3 py-2 border border-black/15 text-[11px] uppercase tracking-luxe text-gray-700 bg-white" value={lang} onChange={(e)=>setLang(e.target.value)}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
            <button onClick={()=>{setMenuOpen(false); setTrackOpen(true);}} className="flex items-center justify-center gap-1.5 px-3 py-2 border border-copper bg-copper/5 text-[11px] uppercase tracking-luxe text-copper">
              <Package size={12}/> {t("track_shipment")}
            </button>
          </div>
        </div>
      )}

      <TrackShipmentModal open={trackOpen} onClose={() => setTrackOpen(false)} />
    </>
  );
}
